/**
 * orderBook viewmodel — turns ONE /order-book/breakdown payload into the chart series
 * and the header cards, on the chosen cost basis. Bars and cards are derived from the
 * SAME rows/totals the DrillTable shows, so chart · tiles · table can never disagree —
 * that is the whole point: they drill together and reconcile (the row LineValue is the
 * bar height; the node totals are the cards). Pure functions, no fetch (CD-7).
 */

import { periodHeading } from '../components/kit/cellFormat'
import type { DrillRow } from '../types/metrics'
import type { OrderBookTotals } from '../types/orderBook'

export type Basis = 'std' | 'actual'

/** The five slots a cost decomposes into. On the ORDER BOOK they are the part's actual
 * build-up and exist on the actual basis only — a part standard is one engineered figure.
 * On MARGIN every row decomposes whichever basis priced it, and the fact serves one cost, so
 * the five tie at every node including Group. Whether a row carries a usable split is
 * therefore not a property of the basis, and componentsOf does not guess — it adds the five
 * up and keeps them only if they tie to the cost being drawn. */
export const COST_COMPONENTS = ['Material', 'Subcontract', 'Machine', 'Labour', 'Other'] as const
export type CostComponent = (typeof COST_COMPONENTS)[number]

/** One bar: `value` is the full height; `cost`/`margin` split it. A null `cost` is an
 * unpriced child (RA — no cost basis), drawn as one "uncosted" segment, GM% absent.
 * `components` splits the cost further where the basis carries a breakdown. */
export interface BreakdownBar {
  label: string
  value: number
  cost: number | null
  margin: number | null // value − cost when priced (may be negative = loss), else null
  gmPct: number | null
  components: Record<CostComponent, number> | null
  /** When this lens does not price the row but the OTHER one does, the other lens's name.
   * "Uncosted" and "costed, just not here" are different facts and must not read alike:
   * Oracle carries £712,368 of quote cost on every line and drew as "No cost basis" on the
   * actual lens, which is the same false claim the guide used to make in words. */
  offLens: string | null
  row: DrillRow
}

const BASIS_LABEL: Record<Basis, string> = { std: 'Standard', actual: 'Actual' }

/** WHERE a row keeps its cost and its margin, and what else it might be costed on.
 *
 * The ORDER BOOK serves two costs per row — a part standard and a job actual — so it has a
 * pair per basis and an `other` to fall back to. THE MARGIN FACT SERVES ONE: its basis is a
 * property of the row (`CostBasis`), not something a reader picks, so it has one pair and no
 * `other`. Passing the columns instead of a basis is what lets one chart kit draw both
 * without a fact deciding it must have two costs to be renderable. */
export interface CostColumns {
  cost: string
  gm: string
  /** The other lens this surface offers, for the "costed, just not here" note. Null on a
   * fact with one cost, where that state cannot arise. */
  other: { cost: string; label: string } | null
}

export const BASIS_COLUMNS: Record<Basis, CostColumns> = {
  actual: { cost: 'ActualCost', gm: 'ActualGM%', other: { cost: 'StdCost', label: BASIS_LABEL.std } },
  std: { cost: 'StdCost', gm: 'StdGM%', other: { cost: 'ActualCost', label: BASIS_LABEL.actual } },
}

/** The margin fact's columns. One cost, one GM, nothing to fall back to. */
export const ONE_COST: CostColumns = { cost: 'Cost', gm: 'GM%', other: null }

const num = (v: DrillRow[string] | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** The row's cost components, but only when the whole set is present and ties back to
 * `cost` — a partial or drifting breakdown is dropped rather than drawn misleadingly.
 *
 * THE TIE IS THE WHOLE TEST, and it is what makes this safe on either basis. A margin row
 * decomposes whichever basis priced it, so a node holding both (one customer buys from both
 * sites) has components that add to neither cost, and this refuses. An order-book row
 * decomposes the actual cost, so asking it on the standard basis refuses the same way. */
function componentsOf(row: DrillRow, cost: number | null): Record<CostComponent, number> | null {
  if (cost === null) return null
  const parts = COST_COMPONENTS.map((key) => num(row[key]))
  if (parts.some((v) => v === null)) return null
  const values = parts as number[]
  const total = values.reduce((sum, v) => sum + v, 0)
  if (Math.abs(total - cost) > 0.01) return null
  return Object.fromEntries(COST_COMPONENTS.map((key, i) => [key, values[i]])) as Record<CostComponent, number>
}

/** A served month id, exactly: '2026-05'. The month columns hold this shape and nothing else
 * does — a customer or a part is never mistaken for one. */
const MONTH_ID = /^\d{4}-(0[1-9]|1[0-2])$/

/**
 * A bar's label. A month axis reads as 'May 26', the same as every other chart on the
 * platform (kit/cellFormat: periodHeading) — the served id '2026-05' is an ADDRESS, and it
 * belongs in the row, the CSV and the URL, not on an axis a person reads.
 *
 * Both the column name AND the value have to agree before this reformats. The two facts name
 * the column differently ('Invoice month' against 'Due month'), so the name alone is not
 * enough; and a part number could in principle look like a date, so the shape alone is not
 * either. Together they cannot collide.
 */
function barLabel(row: DrillRow, labelKey: string): string {
  const raw = row[labelKey]
  if (raw == null) return '—'
  const text = String(raw)
  return labelKey.toLowerCase().includes('month') && MONTH_ID.test(text) ? periodHeading(text) : text
}

/** Bars for the current level: one per child, labelled by `descendKey` (or the leaf's
 * `Line`). Cost/GM read `columns`; an unpriced child keeps cost/margin/gm null. */
export function toBars(rows: DrillRow[], descendKey: string | null, columns: CostColumns): BreakdownBar[] {
  const labelKey = descendKey ?? 'Line'
  return rows.map((row) => {
    const value = num(row.LineValue) ?? 0
    const cost = num(row[columns.cost])
    const elsewhere = columns.other !== null && num(row[columns.other.cost]) !== null
    return {
      label: barLabel(row, labelKey),
      value,
      cost,
      margin: cost === null ? null : value - cost,
      gmPct: num(row[columns.gm]),
      components: componentsOf(row, cost),
      offLens: cost === null && elsewhere ? (columns.other as { label: string }).label : null,
      row,
    }
  })
}

/** How a served level should OPEN, by its grain. A dimension ranks by value — biggest first
 * is the useful read. A month does not: its meaning IS the order, so it opens chronologically
 * and the table agrees with the chart beside it.
 *
 * ⚠ BOTH HALVES SHIPPED WRONG (24 Aug 2026). The server ranked months by value and the table
 * re-sorted by value on top of it, so the month axis read Jun · Jul · May · Apr · Jan · Mar ·
 * Feb — every figure correct and the axis meaningless. Returns undefined for a dimension, so
 * DrillTable keeps its own default rather than having it restated in two places. */
export function defaultSortFor(level: string, descendKey: string | null):
  { field: string; direction: 'asc' | 'desc' } | undefined {
  return level === 'month' && descendKey ? { field: descendKey, direction: 'asc' } : undefined
}

/** The chart can only carry so many bars before it is a smear — the part axis alone runs
 * to hundreds. Cap it, and SAY what fell off: the table below still carries every row and
 * the sum_check still proves the whole set, so this is a legibility limit, never a silent
 * truncation (constitution §3). */
export const CHART_BAR_LIMIT = 25

export interface CappedBars {
  bars: BreakdownBar[]
  /** null when everything fits; otherwise what is off the chart, for the caption. */
  hidden: { count: number; value: number } | null
}

export function capBars(bars: BreakdownBar[], limit = CHART_BAR_LIMIT): CappedBars {
  if (bars.length <= limit) return { bars, hidden: null }
  const ranked = [...bars].sort((a, b) => b.value - a.value)
  const rest = ranked.slice(limit)
  return {
    bars: ranked.slice(0, limit),
    hidden: { count: rest.length, value: rest.reduce((sum, b) => sum + b.value, 0) },
  }
}

/** Cap a TIME axis: keep the most RECENT `limit`, in order.
 *
 * ⚠ capBars CANNOT DO THIS JOB. It ranks by value and takes the biggest, which on a time axis
 * both scrambles the sequence and puts a hole in the middle of it. The month axis serves 67
 * months all-time against a 25-bar limit, so this is the normal case and not an edge one.
 * Dropping the OLDEST is the only truncation a trend survives — the same rule MarginTrend
 * applies with its own 24-month window. */
export function capRecent(bars: BreakdownBar[], limit = CHART_BAR_LIMIT): CappedBars {
  if (bars.length <= limit) return { bars, hidden: null }
  const dropped = bars.slice(0, bars.length - limit)
  return {
    bars: bars.slice(-limit),
    hidden: { count: dropped.length, value: dropped.reduce((sum, b) => sum + b.value, 0) },
  }
}

/** The four header cards on the chosen basis: `value` is every line; cost/GM are
 * costed-only (so they match the per-row GM% and the chart line); coverage = costed ÷ value. */
export interface BreakdownTiles {
  value: number
  cost: number | null
  gm: number | null
  gmPct: number | null
  coveragePct: number | null
  lineCount: number
  /** The denominator GM is taken on — the part of `value` this lens prices. The Value card
   * names it, so `costedValue − cost = gm` is a subtraction the reader can do on the page. */
  costedValue: number | null
  /** Cost this scope holds on the OTHER lens, which this figure therefore excludes. Null on
   * the margin fact, which serves one cost — its tiles are built by viewmodels/marginCoverage
   * and this state cannot arise there. */
  offLens: { cost: number; label: string } | null
}

/** ORDER BOOK ONLY — the margin fact has no basis to choose (viewmodels/marginCoverage).
 *
 * The basis actually worth showing for a scope. Keep the chosen lens, but when it prices
 * nothing here and the OTHER lens does, fall back to that, so a scope with a real cost but
 * not on the default basis shows its margin instead of a blank "no cost basis". The toggle
 * still forces either lens; this only rescues an empty default, and it is a no-op wherever
 * the chosen basis already prices something. */
export function effectiveBasis(totals: OrderBookTotals, basis: Basis): Basis {
  const other: Basis = basis === 'actual' ? 'std' : 'actual'
  return totals[basis].cost === null && totals[other].cost !== null ? other : basis
}

export function toTiles(totals: OrderBookTotals, basis: Basis): BreakdownTiles {
  const b = totals[basis]
  const other: Basis = basis === 'actual' ? 'std' : 'actual'
  const otherCost = totals[other].cost
  return {
    value: totals.value,
    cost: b.cost,
    gm: b.gm,
    gmPct: b.gm_pct,
    coveragePct: b.coverage_pct,
    lineCount: totals.line_count,
    costedValue: b.costed_value,
    offLens: b.cost !== null && otherCost !== null ? { cost: otherCost, label: BASIS_LABEL[other] } : null,
  }
}
